import { memo, useMemo } from 'react';
import { format } from 'date-fns';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useAccountPeople, useTranslation, useTableSort } from '@hooks';
import { AssignmentType } from '@constants/inbox';
import { formatNumber, formatPercentNumber } from '@helpers/formatNumbers';
import { analyticsService } from '@services';
import ExportCSV from '@features/Analytics/ExportCSV';

const SORT_COLUMNS = {
    assignee: 'name',
    conversationsHandled: 'conversationsHandled',
    averageHandlingTime: 'averageHandlingTime',
    resolvedConversations: 'resolvedConversations',
    resolutionRate: 'resolutionRate',
};

const DEFAULT_SORT = { column: 'assignee', dir: 'asc' };

const getTableHeader = t => {
    return [
        {
            key: 0,
            sortKey: 'assignee',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.assignedTo')}
                </Typography>
            ),
        },
        {
            key: 1,
            sortKey: 'conversationsHandled',
            align: 'right',
            component: (
                <Typography variant="subtitle2" align="right">
                    {t('AnalyticsConversations.conversationsHandled')}
                </Typography>
            ),
        },
        {
            key: 2,
            sortKey: 'averageHandlingTime',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsConversations.averageHandleTime')}
                </Typography>
            ),
        },
        {
            key: 3,
            sortKey: 'resolvedConversations',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsConversations.resolvedConversations')}
                </Typography>
            ),
        },
        {
            key: 4,
            sortKey: 'resolutionRate',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsConversations.resolutionRate')}
                </Typography>
            ),
        },
    ];
};

const getTableBody = data => {
    return data.map(assignee => {
        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {assignee.name}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="right"
                        sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}
                    >
                        {formatNumber(assignee.conversationsHandled)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="right"
                        sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}
                    >
                        {formatNumber(assignee.averageHandlingTime)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="right"
                        sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}
                    >
                        {formatNumber(assignee.resolvedConversations)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="right"
                        sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}
                    >
                        {formatPercentNumber(assignee.resolutionRate)}
                    </Typography>
                ),
            },
        ];
    });
};

const ConversationsTable = memo(({ isLoading, data, agents, filter }) => {
    const { t } = useTranslation();
    const { getPersonName } = useAccountPeople();

    const formattedData = useMemo(
        () =>
            data.map(item => ({
                ...item,
                name:
                    item.assigneeType === AssignmentType.Agent
                        ? agents.find(a => a.id === item.assigneeId)?.name
                        : getPersonName(+item.assigneeId),
            })),
        [data, getPersonName, agents],
    );

    const { sort, onSortChange, sortedData } = useTableSort(formattedData, {
        columns: SORT_COLUMNS,
        defaultSort: DEFAULT_SORT,
    });

    const onExport = exportFormat =>
        analyticsService.exportAnalyticsCSV(
            {
                ...filter,
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            'conversations',
            null,
            exportFormat,
        );

    return (
        <Paper elevation={1} sx={{ padding: '24px', width: '100%', borderRadius: '8px' }}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`Header.analyticsConversations`)}
                </Typography>
                <ExportCSV type="conversations" apiCall={onExport} />
            </div>
            <TableContainer>
                <Table
                    data={sortedData}
                    columnNames={getTableHeader(t)}
                    rows={getTableBody(sortedData)}
                    isDataLoading={isLoading}
                    sort={sort}
                    onSortChange={onSortChange}
                />
            </TableContainer>
        </Paper>
    );
});

ConversationsTable.displayName = 'ConversationsTable';

export default ConversationsTable;
